import FadeIn from "@/components/FadeIn";
import Parallax from "@/components/Parallax";
import { DotWaterhole } from "@/components/AboriginalPatterns";
import { TrendingUp } from "lucide-react";

const SectionBadge = ({ icon: Icon, label, color = "primary" }: { icon: any; label: string; color?: string }) => (
  <div className="flex items-center gap-3 mb-5">
    <div className={`p-2.5 rounded-2xl bg-${color}/8 border border-${color}/15`}>
      <Icon size={16} className={`text-${color}`} />
    </div>
    <span className={`text-[10px] font-semibold uppercase tracking-[0.3em] text-${color}`}>{label}</span>
  </div>
);

const CaseNarrativeSection = () => (
  <section className="relative section-padding bg-card/80 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-ochre/[0.02]" />
    <Parallax className="absolute -bottom-16 -left-16" offset={50} offsetX={-20}>
      <DotWaterhole className="text-ochre opacity-12" size={450} />
    </Parallax>
    <div className="relative max-w-3xl mx-auto px-6">
      <FadeIn>
        <SectionBadge icon={TrendingUp} label="Case Narrative" color="primary" />
        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 tracking-tight">
          What the Acquirer Found
        </h2>
      </FadeIn>
      <FadeIn delay={0.1}>
        <div className="space-y-6 text-muted-foreground leading-relaxed text-lg">
          <p>
            A high-growth technology company tripled headcount in under three years.
          </p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-foreground/70 font-medium">
            <span>Revenue accelerating.</span>
            <span>Board confidence high.</span>
            <span>Executive team well regarded.</span>
          </div>
          <p className="text-foreground/80 font-medium">Below the surface:</p>
          <div className="pl-4 border-l-2 border-border/30 space-y-2 text-muted-foreground/80">
            <p>Managers promoted faster than they could be supported.</p>
            <p>Managers making calls without a shared understanding of their authority.</p>
            <p>Standards interpreted differently across teams.</p>
          </div>
          <p className="text-foreground/70 font-medium">No crisis. Just quiet accumulation of risk.</p>
          <p>
            The intervention focused on leadership clarity while the cost of doing so was still low.
          </p>
          <p className="text-foreground/80 font-medium">
            When the acquirer's diligence team arrived, this is what they found:
          </p>
          <div className="pl-4 border-l-2 border-primary/20 space-y-2 text-foreground/80">
            <p>Everyone knew who owned which decisions.</p>
            <p>Leadership expectations were tied to specific, observable behaviours.</p>
            <p>The story told about leadership development matched what was actually happening.</p>
          </div>
          <p>
            The company was acquired for approximately <span className="text-gradient font-semibold">$350 million</span>.
          </p>
          <p>Retention through integration was strong. The leadership layer held.</p>
          <p className="text-foreground font-medium">
            The acquirer did not have to fix the culture post-close.
          </p>
          <p className="text-foreground/70">
            Because the work had already been done.
          </p>
        </div>
      </FadeIn>
    </div>
  </section>
);

export default CaseNarrativeSection;
